// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from 'react';
import { Alert, Button, Card, Center, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';

import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/**
 * The login form.
 *
 * # THE ERROR MESSAGE IS THE SERVER'S, UNCHANGED
 *
 * The server answers every failure — no such user, wrong password, suspended
 * account — with one 401 and one sentence, so that the endpoint is not a
 * user-enumeration oracle. This form must not undo that by guessing at a more
 * helpful message, so it renders `detail` verbatim.
 *
 * The request id is shown alongside, because a user reporting "it said invalid
 * credentials" is far more useful to an operator with one.
 *
 * # WHY THAT MATTERS MORE SINCE 0.2.1
 *
 * The Server now also answers a misconfigured installation — no Organization,
 * or several — with the SAME 401, because telling an anonymous caller which of
 * those it is describes the installation's shape. So an operator staring at
 * "Invalid credentials" may be looking at a configuration problem rather than a
 * typo, and the request id is what connects their screen to the ERROR line in
 * the Server's log that says which.
 */
export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError({
        type: 'about:blank', title: 'Error', status: 0, detail: 'Sign-in failed.',
      }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Center h="100vh">
      <Card withBorder shadow="sm" p="xl" w={420}>
        <form onSubmit={submit}>
          <Stack>
            <Title order={2}>Meshvara</Title>
            <Text c="dimmed" size="sm">One Platform. Every Vendor.</Text>

            {error && (
              <Alert color="red" title={error.problem.title}>
                {error.problem.detail}
                {error.requestId && (
                  <Text size="xs" c="dimmed" mt="xs">
                    Request ID: <code>{error.requestId}</code>
                  </Text>
                )}
              </Alert>
            )}

            {/* EMAIL AND PASSWORD. NOTHING ELSE.
                There was an "Organization ID" field here until 0.2.1, asking for
                a UUID an operator had to look up before they could use the
                product. ADR-005 fixes one Organization per installation, so the
                Server knows it and now resolves it itself. Removed rather than
                hidden or pre-filled: a field that does not exist cannot be used
                to aim a login at another tenant. */}
            <TextInput
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              autoComplete="username"
            />
            <PasswordInput
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
              autoComplete="current-password"
            />
            <Button type="submit" loading={busy} fullWidth>Sign in</Button>
          </Stack>
        </form>
      </Card>
    </Center>
  );
}
